<template>
  <div class="pick-list">
    <b-card header-tag="header">
      <template #header>
        <b-row>
          <!-- ORDER INFO -->
          <b-col cols="8">
            <div>
              <b-card class="header-card">
                <b-card-text>
                  <div class="d-flex order-info">
                    <div
                      v-if="order.note"
                      class="alert alert-dark p-2"
                      role="alert"
                    >
                      <span style="font-size: 0.9rem">
                        {{ order.note }}
                      </span>
                    </div>
                  </div>
                  <dl class="row mb-1">
                    <dt class="col-sm-3">Order</dt>
                    <dd class="col-sm-9">
                      <a class="order-name" @click="goToOrder"
                        >Order {{ order.name }}
                        <b-icon class="small" icon="box-arrow-up-right"
                      /></a>
                    </dd>

                    <dt class="col-sm-3">Name</dt>
                    <dd class="col-sm-9">
                      {{ order.customer.firstName }}
                      {{ order.customer.lastName }}
                    </dd>

                    <dt class="col-sm-3">Open Orders</dt>
                    <dd class="col-sm-9">
                      {{ order.customer.numberOfOrders }}
                      <a
                        :href="getOrderLink(order.customer.email)"
                        target="_blank"
                        class="card-link"
                        >view <b-icon icon="box-arrow-up-right"
                      /></a>
                    </dd>

                    <dt class="col-sm-3">Line Items</dt>
                    <dd class="col-sm-9">
                      <ul class="order-items mb-0">
                        <li v-for="item in order.lineItems" class="text-left">
                          <b-badge pill variant="secondary">{{
                            item.fulfillableQuantity
                          }}</b-badge>
                          {{ item.name }}
                        </li>
                      </ul>
                    </dd>
                  </dl>
                </b-card-text>
              </b-card>
            </div>
          </b-col>

          <!-- WORKFLOW BUTTONS -->
          <b-col cols="4">
            <div class="header-buttons">
              <div>
                <b-button
                  v-if="order.customer.numberOfOrders > 1"
                  class="header-button mb-2"
                  @click="ordersChecked"
                  :variant="[
                    ordersChecked ? 'success' : 'secondary btn-disabled',
                  ]"
                >
                  <div class="header-button-content">
                    <span style="font-size: 90%">Orders Checked</span>
                  </div>
                </b-button>

                <b-button
                  class="header-button mb-2"
                  @click="print"
                  :variant="[
                    listComplete && ordersChecked
                      ? 'success'
                      : 'secondary btn-disabled',
                  ]"
                >
                  <div class="header-button-content">
                    <b-icon :icon="getPrintIcon()" class="mr-2" /><span
                      >Print</span
                    >
                  </div>
                </b-button>

                <b-button
                  class="header-button"
                  @click="finish"
                  :variant="[printed ? 'success' : 'secondary btn-disabled']"
                >
                  <div class="header-button-content">
                    <b-icon :icon="getNextIcon()" class="mr-2" /><span
                      >Finish</span
                    >
                  </div>
                </b-button>
              </div>

              <div>
                <b-row class="m-0 p-0">
                  <b-col cols="7" class="ml-0 pl-0 pr-1">
                    <b-button
                      :disabled="printed"
                      @click="skip"
                      class="header-button mb-0 text-center"
                      >Skip</b-button
                    >
                  </b-col>
                  <b-col cols="5" class="mr-0 pr-0 pl-1">
                    <b-button
                      :disabled="printed"
                      @click="cancel"
                      variant="danger"
                      class="cancel-btn header-button mb-0 text-center"
                      >Cancel</b-button
                    >
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-col>
        </b-row>
      </template>

      <!-- PICKLIST -->
      <b-overlay
        :show="loadingPicklist"
        rounded="sm"
        class="d-flex flex-fix flex-column"
      >
        <b-row class="mb-2">
          <b-col cols="4">
            <b-input-group prepend="Scanner Input">
              <b-form-input
                id="scanner-input"
                placeholder=""
                name="scanner"
                v-on:change="checkBarcode"
                v-model="scannerInput"
                trim
              ></b-form-input>
            </b-input-group>
          </b-col>

          <b-col cols="5">
            <b-toast
              id="scanner-toast"
              :variant="scanStatus"
              no-close-button
              static
              auto-hide-delay="2500"
            >
              <span v-if="scanStatus === 'success'">Item Scan Success</span>
              <span v-else>Item not found in pick list</span>
            </b-toast>
          </b-col>
        </b-row>
        <b-row class="flex-fix w-100">
          <b-col cols="12" class="flex-fix">
            <b-card-text class="d-flex flex-fix">
              <b-list-group class="flex-fix">
                <b-list-group-item
                  v-for="item in picklist.items"
                  class="text-left"
                >
                  <b-row class="w-100">
                    <b-col cols="1" class="p-2">
                      <span
                        ><b-icon
                          :icon="getScanIcon(item.scanned)"
                          :class="[
                            item.scanned ? 'text-success' : 'text-muted',
                          ]"
                          class="p-2 picklist-check"
                        ></b-icon
                      ></span>
                    </b-col>

                    <b-col
                      cols="11"
                      class="pt-3"
                      :class="[item.scanned ? 'scanned' : '']"
                    >
                      <h4 class="mb-0">
                        <span class="small" v-if="item.parent"
                          >{{ item.parent
                          }}<b-icon
                            class="mr-2 ml-2"
                            icon="chevron-right" /></span
                        ><span
                          :style="{ background: item.fulfillmentColor }"
                          :class="{
                            'text-light': colorIsDark(item.fulfillmentColor),
                          }"
                          >{{ item.name | removeDefault }}</span
                        >
                      </h4>
                      <div class="text-muted">
                        <strong>sku: </strong>{{ item.sku }} |<strong>
                          barcode: </strong
                        >{{ item.barcode }}
                      </div>
                    </b-col>
                  </b-row>
                </b-list-group-item>
              </b-list-group>
            </b-card-text>
          </b-col>
        </b-row>
      </b-overlay>
    </b-card>

    <b-modal
      id="next-modal"
      title="Order Not Fulfilled"
      centered
      header-text-variant="danger"
      ok-only
    >
      <p class="my-4">
        This order has not been fulfilled yet. It must be fulfilled before this
        order can be finished.
      </p>
    </b-modal>
    <audio id="audio-error" src="../assets/error.wav" type="audio/wav"></audio>
    <audio
      id="audio-success"
      src="../assets/success.wav"
      type="audio/wav"
    ></audio>

    <b-modal
      id="bad-item-modal"
      title="Invalid Item Scanned"
      centered
      header-text-variant="danger"
      ok-only
    >
      <p class="my-4">
        This item does not belong in the order, please remove it before
        continuing
      </p>
    </b-modal>
  </div>
</template>

<script>
export default {
  name: "PickList",
  props: {
    order: {
      type: Object,
      default: {},
    },
  },
  data() {
    return {
      loadingPicklist: true,
      scannerInput: "",
      scanStatus: "",
      scanSuccess: false,
      listComplete: false,
      printed: false,
      updatingPicklist: false,
      ordersChecked: true,
      enterCommand: false,
    };
  },
  filters: {
    removeDefault(title) {
      return title.replace(" - Default Title", "");
    },
  },
  methods: {
    getScanIcon(scanned) {
      if (scanned) {
        return "check-square";
      } else {
        return "square";
      }
    },
    getPrintIcon() {
      if (this.listComplete && this.ordersChecked) {
        return "unlock-fill";
      } else {
        return "lock-fill";
      }
    },
    getNextIcon() {
      if (this.printed) {
        return "unlock-fill";
      } else {
        return "lock-fill";
      }
    },
    checkBarcode(event) {
      document.getElementById("scanner-input").placeholder = this.scannerInput;
      if (!this.scannerInput) return;
      let scannedItem = this.picklist.items.find((i) => {
        let isMatch = i.barcode === this.scannerInput;
        return isMatch && !i.scanned;
      });
      if (scannedItem) {
        scannedItem.scanned = true;
        this.scanStatus = "success";
      } else {
        this.scanStatus = "danger";
        var sound = document.getElementById("audio-error");
        sound.play();
        setTimeout(sound.pause, 800);
        this.$bvModal.show("bad-item-modal");
      }

      this.$bvToast.show("scanner-toast", {
        solid: true,
      });
      this.scannerInput = "";
      this.checkCompletion();
    },
    colorIsDark(color) {
      console.log("color", color);
      if (!color) return false;
      color = color.substring(1); // remove #
      var rgb = parseInt(color, 16); // convert rrggbb to decimal
      var r = (rgb >> 16) & 0xff; // extract red
      var g = (rgb >> 8) & 0xff; // extract green
      var b = (rgb >> 0) & 0xff; // extract blue
      var luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // per ITU-R BT.709

      if (luma < 40) {
        return true;
      }
    },
    getOrderLink(email) {
      let uri = `https://mode-boards.myshopify.com/admin/orders?selectedView=all&query=${encodeURIComponent(
        email
      )}`;
      return uri;
    },
    checkCompletion() {
      this.listComplete = this.picklist.items.every((i) => i.scanned);
      if (this.listComplete) {
        var sound = document.getElementById("audio-success");
        sound.play();
      }
    },
    print() {
      this.printed = true;
      let location = `https://mode-boards.myshopify.com/admin/orders/${this.order.id}?orderListBeta=true`;
      window.open(location, "_blank");
    },
    goToOrder() {
      let location = `https://mode-boards.myshopify.com/admin/orders/${this.order.id}?orderListBeta=true`;
      window.open(location, "_blank");
    },
    async finish() {
      if (this.updatingPicklist) return;

      this.updatingPicklist = true;
      await this.$store.dispatch("updatePickList", this.picklist);
      const acceptedStatus = ["fulfilled"];
      if (this.picklist.status !== "fulfilled") {
        this.$bvModal.show("next-modal");
      } else {
        bus.$emit("orderFinished", this.order.id);
      }
      this.updatingPicklist = false;
    },
    ordersChecked() {
      this.ordersChecked = true;
    },
    skip() {
      bus.$emit("orderFinished", this.order.id);
    },
    cancel() {
      bus.$emit("orderCancelled", this.order.id);
      this.$store.dispatch("clearPicklist");
    },
  },
  computed: {
    picklist() {
      return this.$store.state.picklist;
    },
  },
  watch: {
    order: {
      immediate: true,
      async handler(newOrder, oldOrder) {
        this.loadingPicklist = true;
        try {
          await this.$store.dispatch("getPickList", newOrder.id);
          this.printed = false;
          this.checkCompletion();
          this.loadingPicklist = false;
        } catch (error) {
          alert(
            "Picklist could not be loaded. Possible causes are missing build codes or inclusion of custom line items."
          );
        }
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.order.customer.numberOfOrders > 1) {
        this.ordersChecked = false;
      }
      document.getElementById("scanner-input").focus();

      // Redirect scans to scanner-input if it's not focused
      document.addEventListener("keydown", function onPress(event) {
        if (this.enterCommand) {
          this.enterCommand = false;
          return;
        }

        if (event.key === "Meta" || event.key === "Control") {
          this.enterCommand = true;
          return;
        }
        if (
          document.activeElement !==
          document.getElementById("order-search-input")
        ) {
          document.getElementById("scanner-input").focus();
        }
      });
    });
  },
};
</script>

<style lang="scss">
.w-100 {
  width: 100%;
}

body {
  ::-webkit-scrollbar {
    width: 10px;
    background-color: lightgrey;
  }

  ::-webkit-scrollbar-track {
    // box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  }

  ::-webkit-scrollbar-thumb {
    background-color: slategrey;
    // outline: 1px solid white;
    border-radius: 10px;
  }
}

.customer-id {
  font-size: 60%;
  cursor: grab;
}

.order-name {
  color: #2c3e50;

  &:hover {
    text-decoration: none;
    cursor: grab;
  }
}

.flex-fix {
  min-height: 0;
  max-height: 100%;
  flex-wrap: nowrap !important;
}

.order-info {
  max-height: 200px;
  flex-direction: column;
}

.order-items {
  display: flex;
  max-height: 150px;
  flex-direction: column;
  min-height: 0;
  overflow-y: scroll;
  padding-inline-start: 0px;
  font-size: 14px;

  li {
    margin-bottom: 5px;
  }
}

.cancel-btn {
  font-size: 20px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  justify-content: center;
}

.pick-list {
  .picklist-check {
    width: 100%;
    height: 100%;
  }

  .small {
    font-size: 70%;
  }

  .list-group {
    min-height: 0;
    flex: 1;
    overflow-y: scroll;
  }

  .list-group-item {
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  }

  .card-body {
    overflow: hidden;
  }

  .form-control {
    &:focus {
      caret-color: grey;
      outline: none;
      -webkit-appearance: none !important;
      border: 1px solid #ced4da;
      box-shadow: none;
      background-color: inherit;
    }
  }

  .scanned {
    color: green;
  }

  .toast {
    box-shadow: none;
    max-height: 38px;

    .toast-body {
      padding: 0.5rem;
    }
  }

  .header-buttons {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    justify-content: space-between;
    height: 100%;
  }

  .header-button {
    height: 45px;
    width: 100%;
    font-weight: bold;
    text-transform: uppercase;
    text-align: left;
    border-radius: 3px;
  }

  .btn-disabled {
    opacity: 0.5;
  }

  .header-button-content {
    display: inline-flex;
  }
}

.card-header {
  text-align: left;
  padding: 20px 45px;
}
</style>
